import { useCallback, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { PAYMENT_NEW, PAYMENT_SUCCESS } from '../../constants/payment'
import { useAppParamsContext } from '../../context/AppParamsContext'
import { useAppUserContext } from '../../context/AppUserContext'
import appUserService from '../../services/appUserService'
import useExternalPaymentRedirect from './useExternalPaymentRedirect'
import { parseFromLocalStorage } from '../../helper/localStorage'

const PARTNER_TYPE = {
  DEFAULT: 'DEFAULT',
  VIETTEL_MONEY: 'VIETTEL_MONEY'
}

const ERROR_CREATE_PAYMENT = {
  UNCONFIRMED_BOOKING_EXISTED: 'Phương tiện của quý khách đã đăng ký thông báo phạt nguội. Vui lòng kiểm tra lại.',
  MAX_LIMIT_SCHEDULE_BY_USER: 'Bạn đã đạt giới hạn số đơn hàng cho phép. Vui lòng liên hệ hỗ trợ để được giải quyết.',
  ORDER_NOT_FOUND: 'Đơn hàng không tồn tại.',
  USER_NOT_FOUND: 'Người dùng không tồn tại.',
  CREATE_PAYMENT_FAILED: 'Tạo đơn hàng thanh toán không thành công.',
  NO_LINK_ACCOUNT_WITH_VTM: 'Chưa liên kết tài khoản Viettel Money.',
  UNAVAILABLE_BALANCE: 'Số dư không khả dụng.',
  PAYMENT_FAILED: 'Thanh toán thất bại.'
}

const PAYMENT_TYPE = {
  ATM_BANK: 1,
  MOMO_BANK: 2,
  GTEL_PAY: 3,
  VNPAY: 4,
  ZALOPAY: 5,
  VIETTEL_PAY: 6,
  TA_MOVE: 7,
  SEPAY: 8,
  VIETTEL_MONEY: 9,
  SHOPEEPAY: 10,
  BAOKIM: 11,
  KIMNGAN: 12,
  VNPAY_INAPP: 13,
  GTELPAY_INAPP: 14,
  VIETTEL_MONEY_GATEWAY: 15
}

const PAYMENT_QR_METHOD = {
  PAYMENT_METHOD_ID: 1, // Thanh toán qua ngân hàng VietQR
  PAYMENT_METHOD_INTERPRISE: 2, // Thanh toán qua ngân hàng VietQR Interprise
  MOMO_PAYMENT_METHOD_ID: 3, // Thanh toán qua MOMO
  GTELPAY_PAYMENT_METHOD_ID: 4, // Thanh toán qua Gtelpay
  ZALOPAY_PAYMENT_METHOD_ID: 5, // Thanh toán qua Zalopay
  VIETQR_INTERPRISE_TAMOVE: 6, // Thanh toán qua ngân hàng VietQR Interprise TAMove
  VNPAY_PAYMENT_METHOD_ID: 7, // Thanh toán qua VNPAY
  SEPAY_PAYMENT_METHOD_ID: 8, // Thanh toán qua Sepay
  SHOPEEPAY_PAYMENT_METHOD_ID: 10, // Thanh toán qua ShopeePay
  BAOKIM_PAYMENT_METHOD_ID: 11, // Thanh toán qua ShopeePay
  KIMNGAN_PAYMENT_METHOD_ID: 12, // Thanh toán qua Kim Ngân
  VNPAYINAPP_PAYMENT_METHOD_ID: 13, // Thanh toán qua VNPAY INAPP
  TAMOVExGTELINAPP_PAYMENT_METHOD_ID: 14, // Thanh toán qua TAMOVExGTEL
  TTDKxGTELINAPP_PAYMENT_METHOD_ID: 15, // Thanh toán qua TTDKxGTEL
  VTMxTAMOVE_PAYMENT_METHOD_ID: 16 // Thanh toán qua TTDKxGTEL
}

export const convertListLicensePlates = (vehicles = []) =>
  vehicles.map((vehicle) => (typeof vehicle === 'string' ? vehicle : vehicle?.plate)).filter(Boolean)

export const convertListProductIds = (services = []) =>
  services.map((service) => (Number(service?.productSize) > 0 ? { productId: service?.productId, quantity: 1 } : service?.productId)).filter(Boolean)

export function getTrafficFinePaymentPartnerType() {
  if (parseFromLocalStorage(process.env.REACT_APP_MINIAPP_VIETTEL_MONEY)) {
    return PARTNER_TYPE.VIETTEL_MONEY
  }

  return PARTNER_TYPE.DEFAULT
}

export function shouldHandleTrafficFinePaymentAtVehicleStep() {
  const partnerType = getTrafficFinePaymentPartnerType()

  if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
    return true
  }

  return false
}

function getPaymentMethodData(paymentMethod, paymentMethodId, paymentMethodType) {
  if (paymentMethod && typeof paymentMethod === 'object') {
    return {
      paymentMethodId: paymentMethod?.paymentMethodId,
      paymentMethodType: paymentMethod?.paymentMethodType
    }
  }

  return {
    paymentMethodId: paymentMethodId ?? paymentMethod,
    paymentMethodType
  }
}

function buildCreateOrderPaymentMethodData(partnerType, paymentMethodData) {
  if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
    return {
      paymentMethodType: PAYMENT_TYPE.VIETTEL_MONEY_GATEWAY,
      paymentMethodId: PAYMENT_QR_METHOD.VTMxTAMOVE_PAYMENT_METHOD_ID
    }
  }

  return {
    paymentMethodId: paymentMethodData?.paymentMethodId,
    paymentMethodType: paymentMethodData?.paymentMethodType
  }
}

function buildCreatePaymentMethodData(partnerType, paymentMethodData, orderDetail) {
  if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
    return {
      paymentMethodType: PAYMENT_TYPE.VIETTEL_MONEY_GATEWAY,
      paymentMethodId: PAYMENT_QR_METHOD.VTMxTAMOVE_PAYMENT_METHOD_ID
    }
  }

  return {
    paymentMethodId: paymentMethodData?.paymentMethodId ?? orderDetail?.paymentMethodId,
    paymentMethodType: paymentMethodData?.paymentMethodType ?? orderDetail?.paymentMethodType
  }
}

function buildAllowRenewValue(partnerType) {
  if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
    return 1
  }

  return 0
}

function getErrorMessage(errorCode, defaultContent) {
  return {
    title: 'Thanh toán thất bại',
    content: ERROR_CREATE_PAYMENT?.[errorCode] || defaultContent
  }
}

function isHttpsUrl(url) {
  return typeof url === 'string' && url.startsWith('https://')
}

export default function useTrafficFinePayment({
  orderDetail = null,
  vehicles = [],
  services = [],
  email,
  paymentMethod = null,
  paymentMethodId,
  paymentMethodType,
  successPath,
  onPaymentSuccess,
  onPaymentPending
} = {}) {
  const history = useHistory()
  const { hidePaymentMethod } = useAppParamsContext()
  const { userData } = useAppUserContext()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [paymentDetail, setPaymentDetail] = useState(null)
  const [isOpenQrCodePayment, setIsOpenQrCodePayment] = useState(false)
  // Order id da tao thanh cong, expose ra de call site theo doi trang thai khi QR dang hien.
  const [pendingOrderId, setPendingOrderId] = useState(null)
  const redirectToExternalPayment = useExternalPaymentRedirect(setIsSubmitting)

  const partnerType = useMemo(() => getTrafficFinePaymentPartnerType(), [])

  const paymentMethodData = useMemo(
    () => getPaymentMethodData(paymentMethod, paymentMethodId, paymentMethodType),
    [paymentMethod, paymentMethodId, paymentMethodType]
  )

  const phoneNumber = useMemo(() => {
    return userData?.phoneNumberWithUsernamePrefix || orderDetail?.phoneNumber || ''
  }, [orderDetail?.phoneNumber, userData?.phoneNumberWithUsernamePrefix])

  const handleCheckAuthenticationLinked = useCallback(
    async (createdOrderId) => {
      if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
        setIsSubmitting(true)
        const linkedResponse = await appUserService.viettelMoneyAPIInitializeLink({
          username: phoneNumber,
          orderId: createdOrderId
        })

        if (linkedResponse?.statusCode !== 200) {
          setIsSubmitting(false)
          setErrorMessage({
            title: 'Liên kết thất bại',
            content: 'Liên kết tài khoản thất bại, vui lòng thử lại.'
          })
          return false
        }

        if (linkedResponse?.data?.isLinked) {
          setIsSubmitting(false)
          return true
        }

        if (linkedResponse?.data?.deepLink) {
          redirectToExternalPayment(linkedResponse.data.deepLink)
          return false
        }

        setIsSubmitting(false)
        setErrorMessage({
          title: 'Liên kết thất bại',
          content: 'Không thể kiểm tra liên kết tài khoản. Vui lòng thử lại.'
        })
        return false
      }

      return true
    },
    [partnerType, phoneNumber, redirectToExternalPayment]
  )

  const handlePayment = useCallback(async () => {
    try {
      if (isSubmitting) {
        return
      }

      // 1. Nếu đã có đơn thì dùng lại orderId cũ, chưa có thì tạo đơn mới.
      let customerScheduleId = orderDetail?.orderId

      if (!customerScheduleId) {
        const payloadCreateOrder = {
          email,
          licensePlatesList: convertListLicensePlates(vehicles),
          productIds: convertListProductIds(services),
          phoneNumber,
          requestId: `REQ-${Date.now()}`,
          allowRenew: buildAllowRenewValue(partnerType),
          vehicleType: vehicles?.[0]?.vehicleType,
          ...buildCreateOrderPaymentMethodData(partnerType, paymentMethodData)
        }

        setIsSubmitting(true)
        const orderResult = await appUserService.createOrderAutoNoty(payloadCreateOrder)
        setIsSubmitting(false)

        if (orderResult?.statusCode !== 200 || !orderResult?.data) {
          const nextErrorMessage = getErrorMessage(orderResult?.error, 'Có lỗi xảy ra khi tạo đơn thanh toán. Vui lòng thử lại.')
          setErrorMessage(nextErrorMessage)
          return
        }

        customerScheduleId = orderResult?.data?.orderId
      }

      // 2. Với đối tác cần liên kết tài khoản thì kiểm tra liên kết trước khi thanh toán.
      const isLinked = await handleCheckAuthenticationLinked(customerScheduleId)
      if (!isLinked) {
        return
      }

      // 3. Tạo thanh toán cho đơn vừa có.
      const payloadCreatePayment = {
        customerScheduleId,
        ...buildCreatePaymentMethodData(partnerType, paymentMethodData, orderDetail)
      }

      setIsSubmitting(true)
      const paymentResponse = await appUserService.createPayment(payloadCreatePayment)

      if (paymentResponse?.statusCode !== 200 || !paymentResponse?.data) {
        setIsSubmitting(false)
        const nextErrorMessage = getErrorMessage(paymentResponse?.error, 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.')
        setErrorMessage(nextErrorMessage)
        return
      }

      setPaymentDetail(paymentResponse.data)

      const paymentLink = paymentResponse?.data?.paymentLinkUrl
      const paymentDeepLink = paymentResponse?.data?.paymentDeepLink

      if (partnerType === PARTNER_TYPE.VIETTEL_MONEY) {
        if (isHttpsUrl(paymentDeepLink)) {
          redirectToExternalPayment(paymentDeepLink)
          return
        }

        setIsSubmitting(false)
        setErrorMessage({
          title: 'Thanh toán thất bại',
          content: 'Không thể khởi tạo liên kết thanh toán Viettel Money. Vui lòng thử thanh toán lại.'
        })
        return
      }

      if (isHttpsUrl(paymentLink)) {
        redirectToExternalPayment(paymentLink)
        return
      }

      setIsSubmitting(false)
      if (paymentResponse?.data?.paymentStatus === PAYMENT_SUCCESS) {
        if (typeof onPaymentSuccess === 'function') {
          onPaymentSuccess({
            customerScheduleId,
            paymentDetail: paymentResponse.data
          })
        } else if (successPath) {
          history.push(successPath.replace(':id', customerScheduleId))
        }

        return
      }

      if (paymentResponse?.data?.paymentStatus === PAYMENT_NEW) {
        setPendingOrderId(customerScheduleId)
        setIsOpenQrCodePayment(true)

        if (typeof onPaymentPending === 'function') {
          onPaymentPending({
            customerScheduleId,
            paymentDetail: paymentResponse.data
          })
        }

        return
      }

      const nextErrorMessage = {
        title: 'Thanh toán thất bại',
        content: 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.'
      }
      setErrorMessage(nextErrorMessage)
      return
    } catch (error) {
      setIsSubmitting(false)
      setErrorMessage({
        title: 'Thanh toán thất bại',
        content: 'Có lỗi xảy ra khi thanh toán. Vui lòng thử lại.'
      })
    }
  }, [
    email,
    handleCheckAuthenticationLinked,
    history,
    isSubmitting,
    onPaymentPending,
    onPaymentSuccess,
    orderDetail,
    partnerType,
    paymentMethodData,
    phoneNumber,
    redirectToExternalPayment,
    services,
    successPath,
    vehicles
  ])

  const disabledPayBtn = useMemo(() => {
    const needChoosePaymentMethod = partnerType === PARTNER_TYPE.DEFAULT && !(hidePaymentMethod || orderDetail?.orderId)
    const hasPaymentMethod = !!(paymentMethodData?.paymentMethodId || paymentMethodData?.paymentMethodType)

    return isSubmitting || vehicles.length === 0 || (needChoosePaymentMethod && !hasPaymentMethod)
  }, [hidePaymentMethod, isSubmitting, orderDetail?.orderId, partnerType, paymentMethodData, vehicles.length])

  return {
    isSubmitting,
    errorMessage,
    setErrorMessage,
    paymentDetail,
    isOpenQrCodePayment,
    setIsOpenQrCodePayment,
    pendingOrderId,
    disabledPayBtn,
    handlePayment
  }
}
